import { useEffect } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'



import { useProductSort } from '@entities/Product'

import { useAddressStore } from '@features/shipping-methods'

import NotFoundComponent from '@widgets/not-found/NotFoundComponent'
import { ProductPreviewCard } from '@widgets/product-preview'



import { QUERY_NAMES } from '@shared/contants'
import {
  NestedFilterInput,
  Product,
  ProductsPreviewCardQuery,
  ProductsPreviewCardQueryVariables,
  useProductsPreviewCardQuery,
} from '@shared/generated'
import { useFilters } from '@shared/hooks'
import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'
import { DynamicFilters } from '@shared/types'
import CardSkeleton from '@shared/ui/card-skeleton'
import { mapFiltersToApi } from '@shared/uttils/mapFiltersToApi'

import styles from './styles.module.scss'

const LIMIT = 12

type ProductsProps = {
  onTotalChange?: (total: number | null) => void;
  categoryId?: string | null;
  loadingCategory?: boolean;
};

function ProductsLoading () {
  return (
    <div className={styles.root}>
      {Array.from({ length: 8 }).map((_, index) => (
        <CardSkeleton key={index} />
      ))}
    </div>
  )
}

export default function Products ({ onTotalChange, categoryId, loadingCategory }: ProductsProps) {
  const { sortConfig } = useProductSort()
  const { filters } = useFilters<DynamicFilters>({
    groupKey: QUERY_NAMES.PRODUCT_FILTERS,
  })
  const apiFilters = mapFiltersToApi(filters)
  const { shopId } = useAddressStore()

  const { data, loading, fetchMore } = useProductsPreviewCardQuery({
    variables: {
      shopId: shopId,
      categoryId,
      pager: { page: 1, limit: LIMIT },
      sort: {
        fieldName: sortConfig?.fieldName,
        direction: sortConfig?.direction,
      },
      filter: apiFilters as NestedFilterInput,
    },
    skip: !categoryId,
  })

  const items =
    data?.products?.edges?.filter((item): item is Product => item !== null) ||
    []
  const total = data?.products?.pageInfo?.total ?? 0

  useEffect(() => {
    onTotalChange?.(total)
  }, [ total, onTotalChange ])

  const { hasMore, loadingMore, fetchNextPage, clearPagination } =
    useFetchMorePagination<
      ProductsPreviewCardQuery,
      ProductsPreviewCardQueryVariables
    >({
      loading,
      fetchMore,
      pageVariable: 'pager',
      usePager: true,
      variables: {
        shopId,
        categoryId,
        pager: { limit: LIMIT },
        sort: {
          fieldName: sortConfig?.fieldName,
          direction: sortConfig?.direction,
        },
        filter: apiFilters as NestedFilterInput,
      },
      updaterFunction (cached, fetched) {
        const prev = cached?.products?.edges ?? []
        const curr = fetched?.products?.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            products: {
              ...cached.products,
              edges: [ ...prev, ...curr ],
            },
          } as ProductsPreviewCardQuery,
        }
      },
    })

  useEffect(() => {
    clearPagination()
  }, [ categoryId, filters, sortConfig, clearPagination ])

  if ((loading && items.length === 0) || loadingCategory) {
    return <ProductsLoading />
  }

  if (!categoryId) {
    return (
      <NotFoundComponent />
    )
  }

  return (
    <InfiniteScroll
      dataLength={items.length}
      next={fetchNextPage}
      hasMore={hasMore}
      loader={loadingMore && <ProductsLoading />}
    >
      <section className={styles.root}>
        {items.map((p) => (
          <ProductPreviewCard
            key={p.id}
            className={styles.card}
            data={p as Product}
          />
        ))}
      </section>
    </InfiniteScroll>
  )
}
